import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';

import { EntityUpdate } from '../taskAuthorizations';
import { getBranchCodesMappedToVendors, loadVendorsNickNames } from '../../apiClient';
import { wfpFormat } from '../../utils/utils';

interface Props {
    updates: EntityUpdate[];
    entityId?: string;
    groupName?: string;
}

export function ProductPriceVersionChangeList({ updates, entityId, groupName }: Props) {
    const [branchMap, setBranchMap] = useState<Record<number, { vendorId: string; outlet: string }>>({});
    const [vendorNames, setVendorNames] = useState<Record<string, string>>({});
    const [oldBranchIds, setOldBranchIds] = useState<number[] | null>(null);

    useEffect(() => {
        (async () => {
            try {
                type BranchCodeShape = {
                    id: number | string;
                    branchCode?: string;
                    branch_code?: string;
                    outlet?: string;
                };
                type VendorGroupShape = {
                    vendorId?: string | number;
                    vendor_id?: string | number;
                    branchCodes?: BranchCodeShape[];
                    branch_codes?: BranchCodeShape[];
                };
                const groups: VendorGroupShape[] = await getBranchCodesMappedToVendors();
                const map = (groups || []).reduce(
                    (acc, group) => {
                        const vendorId = String(group.vendorId ?? group.vendor_id);
                        const branchCodes = (group.branchCodes ?? group.branch_codes ?? []) as BranchCodeShape[];
                        branchCodes.forEach((bc) => {
                            const id = Number(bc.id);
                            const outlet = bc.branchCode ?? bc.branch_code ?? bc.outlet ?? '';
                            if (!Number.isNaN(id)) {
                                acc[id] = { vendorId, outlet };
                            }
                        });
                        return acc;
                    },
                    {} as Record<number, { vendorId: string; outlet: string }>
                );
                setBranchMap(map);
            } catch {}
        })();
    }, []);

    useEffect(() => {
        (async () => {
            try {
                const list: Array<{ id: string | number; name?: string; nickName?: string }> =
                    await loadVendorsNickNames({} as never);
                const mapping = (list || []).reduce(
                    (acc: Record<string, string>, v) => {
                        const id = String(v.id);
                        const name = v.nickName || v.name || id;
                        acc[id] = name;
                        return acc;
                    },
                    {} as Record<string, string>
                );
                setVendorNames(mapping);
            } catch {}
        })();
    }, []);

    useEffect(() => {
        if (!entityId) return;
        const branchCodeIdsUpdate = updates.find((u) => u.path === 'branchCodeIds');
        if (Array.isArray(branchCodeIdsUpdate?.oldValue) && branchCodeIdsUpdate.oldValue.length > 0) {
            const ids = (branchCodeIdsUpdate.oldValue as Array<number | string>)
                .map(Number)
                .filter((n) => !Number.isNaN(n));
            setOldBranchIds(ids);
            return;
        }
    }, [entityId, updates]);

    const displayed = useMemo(() => {
        const toVendorName = (id: number): string => {
            const info = branchMap[id];
            if (!info) return String(id);
            return vendorNames[info.vendorId] || info.vendorId;
        };
        const outletOf = (id: number): string => branchMap[id]?.outlet || '';

        return updates.map((u) => {
            if (u.path !== 'branchCodeIds') return u;
            const newIds = Array.isArray(u.newValue)
                ? (u.newValue as Array<number | string>).map((v) => Number(v)).filter((n) => !Number.isNaN(n))
                : [];
            const oldIds = Array.isArray(oldBranchIds) ? oldBranchIds : [];
            const oldSet = new Set(oldIds);
            const newSet = new Set(newIds);
            const removed = oldIds.filter((id) => !newSet.has(id));
            const added = newIds.filter((id) => !oldSet.has(id));

            const grouped: Record<string, { removed: number[]; added: number[] }> = {};
            const ensure = (outlet: string) => (grouped[outlet] ||= { removed: [], added: [] });
            removed.forEach((id) => ensure(outletOf(id)).removed.push(id));
            added.forEach((id) => ensure(outletOf(id)).added.push(id));

            const diffNodes: React.ReactNode[] = Object.keys(grouped)
                .sort()
                .map((outlet) => {
                    const { removed: r, added: a } = grouped[outlet];
                    const nodes: React.ReactNode[] = [];
                    nodes.push(
                        <div key={`h-${outlet}`} style={{ fontWeight: 600 }}>
                            {outlet || 'Unknown outlet'}
                        </div>
                    );
                    r.sort((x, y) => toVendorName(x).localeCompare(toVendorName(y))).forEach((id) =>
                        nodes.push(
                            <div
                                key={`r-${outlet}-${id}`}
                                style={{ color: 'var(--red, #c62828)', textDecoration: 'line-through' }}
                            >
                                {toVendorName(id)}
                            </div>
                        )
                    );
                    a.sort((x, y) => toVendorName(x).localeCompare(toVendorName(y))).forEach((id) =>
                        nodes.push(
                            <div key={`g-${outlet}-${id}`} style={{ color: 'var(--green, #2e7d32)' }}>
                                {toVendorName(id)}
                            </div>
                        )
                    );
                    return <div key={`grp-${outlet}`}>{nodes}</div>;
                });

            return {
                ...u,
                path: `branches for group ${groupName || entityId}`,
                newValue: diffNodes,
            } as EntityUpdate;
        });
    }, [updates, branchMap, vendorNames, oldBranchIds, entityId, groupName]);

    return (
        <div style={{ display: 'flex', flexDirection: 'row' }}>
            <table className={`wfp-table--striped mt4 table-hover`}>
                <thead>
                    <tr>
                        <th>Property</th>
                        <th>New value</th>
                    </tr>
                </thead>
                <tbody>
                    {displayed.map((update) => (
                        <tr key={update.path}>
                            <td>{update.path ? wfpFormat(update.path) : '-'}</td>
                            <td>{renderValue(update.newValue)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function renderValue(value: unknown): React.ReactNode {
    if (Array.isArray(value)) {
        return (
            <div>
                {(value as Array<React.ReactNode>).map((text, idx: number) => (
                    <div key={idx}>{text}</div>
                ))}
            </div>
        );
    }
    if (value === true) return 'Yes';
    if (value === false) return 'No';
    return (value ?? '-') as React.ReactNode;
}
